import { useCompanyMembers } from '@commenda/api';
import { setTaskAssignee } from '@commenda/api/service-request/setTaskAssignee';
import { addUserToCompany } from '@commenda/api/user/addUserToCompany';
import {
  Avatar,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Dropdown,
  type DropdownMenuProps,
  Icons,
  Input,
  Label,
  Tooltip,
} from '@commenda/kakashi';
import { cn, filterCommendaIoEmails } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { TaskParty, UserEntityLinkRole } from '@prisma/client';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useNotification } from '../../../../hooks/useNotification';
import { ServiceRequestTaskProps } from '../../../types';

const inviteFormSchema = z.object({
  name: z.string().trim().min(1, { message: 'Enter a name' }),
  email: z
    .string()
    .trim()
    .min(1, { message: 'Enter an email' })
    .email({ message: 'Enter a valid email address' }),
});

type InviteFormFields = z.infer<typeof inviteFormSchema>;

function InitialsAvatar({
  name,
  seed,
  tooltip,
  interactive,
}: {
  name?: string | null;
  /** Stable user id for the deterministic color; falls back to name. */
  seed?: string | null;
  tooltip: string;
  interactive?: boolean;
}) {
  if (!name) return null;

  const avatar = (
    <Avatar
      size="xs"
      colorSeed={seed ?? name}
      name={name}
      className={cn(interactive && 'cursor-pointer')}
    />
  );

  // Skip the hover tooltip when the avatar is interactive — the click-to-open
  // popover shows the member list with the name, so the tooltip would overlap it.
  if (interactive) return avatar;

  return <Tooltip content={tooltip}>{avatar}</Tooltip>;
}

function UnassignedAvatar({ interactive }: { interactive?: boolean }) {
  const body = (
    <div
      className={cn(
        'size-6 rounded-full border-2 border-dashed border-border-secondary bg-bg-primary flex items-center justify-center',
        interactive && 'cursor-pointer hover:border-fg-brand'
      )}
    >
      <Icons.Plus className="size-3 text-text-tertiary" />
    </div>
  );

  if (interactive) return body;

  return <Tooltip content="Assign to a team member">{body}</Tooltip>;
}

function CommendaLogoAvatar({ tooltip }: { tooltip: string }) {
  return (
    <Tooltip content={tooltip}>
      <div className="size-6 rounded-full bg-white border border-border-secondary flex items-center justify-center overflow-hidden">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/commenda-new-logo.svg"
          alt="Commenda Logo"
          className="size-4 object-contain"
        />
      </div>
    </Tooltip>
  );
}

function InviteAndAssignModal({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (args: InviteFormFields) => Promise<void>;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InviteFormFields>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: { name: '', email: '' },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const submit = async (values: InviteFormFields) => {
    await onSubmit(values);
    reset();
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent className="w-[480px]">
        <DialogHeader>
          <DialogTitle>Invite and assign to task</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(submit)}>
          <div className="px-6 py-4 flex flex-col gap-lg">
            <div className="flex flex-col gap-sm">
              <Label htmlFor="invite-name">Name</Label>
              <Input
                id="invite-name"
                type="text"
                placeholder="Jane Doe"
                autoFocus
                {...register('name')}
                error={errors.name?.message}
              />
            </div>
            <div className="flex flex-col gap-sm">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="email@example.com"
                {...register('email')}
                error={errors.email?.message}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Inviting…' : 'Invite and assign'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TaskAssigneePicker({
  taskId,
  companyId,
  currentAssigneeId,
  trigger,
  onChanged,
}: {
  taskId: number;
  companyId: number;
  currentAssigneeId: string | null;
  trigger: React.ReactNode;
  onChanged: () => void | Promise<unknown>;
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const { data: companyUsers, mutate: mutateCompanyMembers } =
    useCompanyMembers(companyId);
  const { setNotification } = useNotification();

  const members = useMemo(() => {
    const all = filterCommendaIoEmails(companyUsers?.members, companyId) ?? [];
    // Drop entries without a usable display name/email to avoid blank-avatar rows.
    return all.filter((m) => (m.name ?? m.email ?? '').trim().length > 0);
  }, [companyUsers?.members, companyId]);

  const handleInviteAndAssign = useCallback(
    async ({ name, email }: { name: string; email: string }) => {
      const normalized = email.trim().toLowerCase();
      try {
        const addResponse = await addUserToCompany(companyId, {
          name,
          email: normalized,
          role: UserEntityLinkRole.USER,
        });
        if (!addResponse.ok) throw new Error('add failed');

        // Refetch company members so the new user's id is available locally.
        const updated = (await mutateCompanyMembers()) as
          | { members?: { id: string; email: string }[] }
          | undefined;

        const newMember = updated?.members?.find(
          (m) => m.email?.toLowerCase() === normalized
        );
        if (!newMember) throw new Error('new member not found after invite');

        await setTaskAssignee({ taskId, userId: newMember.id });
        await onChanged();
      } catch {
        setNotification({
          show: true,
          valid: false,
          title: 'Could not invite and assign',
          message:
            'We could not complete the invite. Check the email and try again.',
        });
        throw new Error('invite flow failed');
      }
    },
    [companyId, mutateCompanyMembers, taskId, onChanged, setNotification]
  );

  const handlePick = useCallback(
    async (userId: string) => {
      if (userId === currentAssigneeId) return;
      setIsSaving(true);
      try {
        await setTaskAssignee({ taskId, userId });
        await onChanged();
      } catch {
        setNotification({
          show: true,
          valid: false,
          title: 'Could not update assignee',
          message: 'Please try again in a moment.',
        });
      } finally {
        setIsSaving(false);
      }
    },
    [taskId, currentAssigneeId, onChanged, setNotification]
  );

  const menu: DropdownMenuProps = {
    groups: [
      {
        label: 'Team members',
        items:
          members.length === 0
            ? [
                {
                  label: 'No team members yet',
                  disabled: true,
                  className: 'min-h-11',
                  labelClassName: 'text-text-tertiary',
                },
              ]
            : members.map((member) => {
                const displayName = member.name ?? member.email ?? '';
                const isCurrent = member.id === currentAssigneeId;
                const MemberAvatarIcon: React.ComponentType<{
                  className: string;
                }> = () => (
                  <Avatar
                    size="sm"
                    name={displayName}
                    colorSeed={member.id}
                    className="shrink-0 mr-md"
                    image={member.image}
                  />
                );
                return {
                  icon: MemberAvatarIcon,
                  label: displayName,
                  shortcut: isCurrent ? (
                    <Icons.Check className="size-4 text-text-primary" />
                  ) : undefined,
                  className: cn('min-h-11', isCurrent && '!bg-bg-secondary'),
                  onClick: () => handlePick(member.id),
                };
              }),
      },
      {
        label: 'New user',
        items: [
          {
            icon: Icons.Send01,
            label: 'Invite and assign',
            className: 'min-h-11',
            onClick: () => setInviteOpen(true),
          },
        ],
      },
    ],
  };

  return (
    <>
      <Dropdown
        menu={menu}
        align="end"
        contentClassName="w-[280px] p-xs"
        itemClassName="px-md py-sm gap-sm"
        labelClassName="txt-extra-small-medium text-text-primary"
        groupLabelClassName="txt-extra-small-bold text-text-muted"
        hideSeparators
      >
        <button
          type="button"
          className="inline-flex p-0 m-0 bg-transparent border-0 focus:outline-none"
          onClick={(e) => e.stopPropagation()}
          disabled={isSaving}
        >
          {trigger}
        </button>
      </Dropdown>
      <InviteAndAssignModal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSubmit={handleInviteAndAssign}
      />
    </>
  );
}

const ServiceRequestCardAvatar = ({
  state,
  task,
  context,
}: Omit<ServiceRequestTaskProps, 'config'>): React.ReactElement | null => {
  const viewerIsAgent = state.role === TaskParty.AGENT;
  const taskForClient = task.pendingOn === TaskParty.CLIENT;

  // For CLIENT-side tasks, prefer the per-task AssignedUser. Fall back to the
  // first SR-level assignee for tasks that haven't been individually assigned yet.
  if (taskForClient) {
    const assigned = task.AssignedUser ?? null;
    const fallbackName =
      context.serviceRequest?.ServiceRequestAssignees?.[0]?.User?.name ??
      undefined;

    const displayName = assigned?.name ?? fallbackName;
    const tooltip = viewerIsAgent
      ? assigned?.name
        ? `Assigned to ${assigned.name}`
        : 'Task for customer'
      : (displayName ?? 'Customer');

    const trigger = displayName ? (
      <InitialsAvatar
        name={displayName}
        seed={assigned?.id}
        tooltip={tooltip}
        interactive
      />
    ) : (
      <UnassignedAvatar interactive />
    );

    const companyId = context.serviceRequest?.companyId;
    // Only the customer side can reassign for now; agents see a read-only avatar.
    if (viewerIsAgent || !companyId) {
      return displayName ? (
        <InitialsAvatar
          name={displayName}
          seed={assigned?.id}
          tooltip={tooltip}
        />
      ) : (
        <UnassignedAvatar />
      );
    }

    return (
      <TaskAssigneePicker
        taskId={task.id}
        companyId={companyId}
        currentAssigneeId={assigned?.id ?? null}
        trigger={trigger}
        onChanged={() => context.mutate()}
      />
    );
  }

  const agentName =
    context.serviceRequest?.assignedAgentFirmUser?.user?.name ?? undefined;
  const tooltip = viewerIsAgent ? (agentName ?? 'Agent') : 'Task for agent';
  return <CommendaLogoAvatar tooltip={tooltip} />;
};

export { ServiceRequestCardAvatar };
